// src/App.js (Refactored - No Featured Products)
import React, { useState, useEffect, useCallback } from 'react';

// Import shared API utility
import { callApi } from './api/api';
import { getCleanImageUrl } from './utils/imageHelper';

import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth, messaging } from './firebase'; 
import { getToken, onMessage } from 'firebase/messaging';

// Import Layout Components
import Header from './layout/Header';
import Footer from './layout/Footer';
import BottomNav from './components/BottomNav';
import CartBottomBar from './components/CartBottomBar';


// Import Common Components
import MessageBox from './common/MessageBox';
import InstallAppBanner from './components/InstallAppBanner';

// Import Feature View Components
import ProductView from './products/ProductView';
import ProductDetailsView from './products/ProductDetailsView';
import CartView from './cart/CartView';
import LoginRegisterView from './auth/LoginRegisterView';
import UploadPrescriptionView from './prescriptions/UploadPrescriptionView';
import OrdersView from './orders/OrdersView';
import TrackOrderView from './orders/TrackOrderView';
import ProfileView from './profile/ProfileView';
import LandingPageView from './LandingPageView'; //
import AdminView from './admin/AdminView'; // Updated import
import PaymentView from './PaymentView'; // Added import
import DriverDashboard from './driver/DriverDashboard';
import HealthInsuranceFormView from './insurance/HealthInsuranceFormView';
import LabsView from './labs/LabsView';
import AboutUsView from './company/AboutUsView';
import CareersView from './company/CareersView';
import JoinUsView from './company/JoinUsView';
import CorporatesView from './company/CorporatesView';
import BlogView from './company/BlogView';
// Import Legal/Content Views
import HelpCenterView from './legal/HelpCenterView';
import PrivacyPolicyView from './legal/PrivacyPolicyView';
import TermsConditionsView from './legal/TermsConditionsView';
import ReturnPolicyView from './legal/ReturnPolicyView';


const App = () => {
    const [activePage, setActivePage] = useState('landing');
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState(''); // For LoginRegisterView
    const [otpStep, setOtpStep] = useState(false);
    const [registrationStep, setRegistrationStep] = useState(false);
    const [forgotPasswordStep, setForgotPasswordStep] = useState(false);
    const [pendingIdToken, setPendingIdToken] = useState(null);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [userRole, setUserRole] = useState('user'); // Replaced isAdmin with userRole
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserMobile, setCurrentUserMobile] = useState(null); // New state for user mobile
    const [currentUserName, setCurrentUserName] = useState(null); // New state for user name
    const [currentUserAddress, setCurrentUserAddress] = useState(null); // New state for user address
    const [currentUserAddresses, setCurrentUserAddresses] = useState([]); // New state for multiple addresses
    const [currentUserFamilyMembers, setCurrentUserFamilyMembers] = useState([]); // New state for family members
    const [currentUserWalletBalance, setCurrentUserWalletBalance] = useState(0); // New state for wallet
    const [pendingMobile, setPendingMobile] = useState(''); // Store mobile during auth flow
    const [pendingRedirect, setPendingRedirect] = useState(null); // Track post-login redirects
    const [cart, setCart] = useState(() => {
        const savedCart = localStorage.getItem('cart');
        console.log("[Cart Debug] Initial load from localStorage:", savedCart);
        if (savedCart) {
            try {
                const parsed = JSON.parse(savedCart);
                console.log("[Cart Debug] Parsed successfully:", parsed);
                return parsed;
            } catch (e) {
                console.error("[Cart Debug] Failed to parse saved cart on load", e);
                return [];
            }
        }
        return [];
    });
    const [message, setMessage] = useState(''); // For global message box
    const [messageType, setMessageType] = useState('info'); // For global message box type
    const [currentOrderId, setCurrentOrderId] = useState(null); // For PaymentView
    const [currentOrderTotal, setCurrentOrderTotal] = useState(null); // For PaymentView
    const [currentOrderStoreBillUrl, setCurrentOrderStoreBillUrl] = useState(null); // For PaymentView

    const [products, setProducts] = useState([]);
    const [userLocation, setUserLocation] = useState('');
    const [userCoordinates, setUserCoordinates] = useState(null); // New state for coords
    const [currentPage, setCurrentPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTherapyAreaFilter, setSelectedTherapyAreaFilter] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Add loading state

    // Save cart to local storage whenever it changes
    useEffect(() => {
        console.log("[Cart Debug] Saving to localStorage:", cart);
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    // Effect for initializing location and coordinates from local storage
    useEffect(() => {
        const savedLocation = localStorage.getItem('userLocation');
        if (savedLocation) {
            setUserLocation(savedLocation);
        }
        
        const savedCoords = localStorage.getItem('userCoordinates');
        if (savedCoords) {
            try {
                setUserCoordinates(JSON.parse(savedCoords));
            } catch (e) {
                console.error("Failed to parse saved coords", e);
            }
        }
    }, []);

    // Check for token on initial load and handle hash-based deep linking
    useEffect(() => {
        const token = localStorage.getItem('userToken');
        const userInfo = localStorage.getItem('userInfo');

        if (token && userInfo) {
            try {
                const user = JSON.parse(userInfo);
                setIsAuthenticated(true);
                setCurrentUserId(user._id);
                // Fallback for older sessions that only had isAdmin boolean
                if (user.role) {
                    setUserRole(user.role);
                } else if (user.isAdmin) {
                    setUserRole('admin');
                } else {
                    setUserRole('user');
                }
                setCurrentUserMobile(user.mobile || null);
                setCurrentUserName(user.name || null);
                setCurrentUserAddress(user.address || null);
                setCurrentUserAddresses(user.addresses || []);
                setCurrentUserFamilyMembers(user.familyMembers || []);
                setCurrentUserWalletBalance(user.walletBalance || 0);

                // Handle deep link to admin if hash is #admin and user is actually an admin
                if (window.location.hash === '#admin' && (user.role === 'admin' || user.role === 'super_admin' || user.isAdmin)) {
                    setActivePage('admin-orders');
                }
            } catch (e) {
                console.error("Failed to parse user info", e);
                localStorage.removeItem('userToken');
                localStorage.removeItem('userInfo');
            }
        }

        // Silent token refresh on boot if permission was previously granted
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            requestNotificationPermission();
        }

        const handleHashChange = async () => {
            const hash = window.location.hash;
            if (hash.startsWith('#product/')) {
                const productId = hash.replace('#product/', '');
                setSelectedProductId(productId);
                setActivePage('product-details');
            } else if (hash.startsWith('#pay/')) {
                const orderId = hash.replace('#pay/', '');
                setCurrentOrderId(orderId);
                
                // Fetch the order amount to pre-fill
                try {
                    const responseData = await callApi(`/orders/${orderId}`);
                    if (responseData && responseData.order) {
                        if (responseData.order.total) setCurrentOrderTotal(responseData.order.total);
                        if (responseData.order.storeBillUrl) setCurrentOrderStoreBillUrl(responseData.order.storeBillUrl);
                    }
                } catch (e) {
                    console.error("Could not fetch order total for payment", e);
                }
                
                setActivePage('payment');
            } else if (hash === '' && activePage === 'product-details') {
                // If user goes back to a page without a hash
                setActivePage('landing');
            }
        };

        // Check hash on initial load
        handleHashChange();

        // Listen for hash changes (e.g. user clicks back/forward button)
        window.addEventListener('hashchange', handleHashChange);

        // Foreground notification handler
        let unsubscribe = () => {};
        
        // Use a tiny timeout to let isSupported() resolve in firebase.js
        setTimeout(() => {
            if (messaging) {
                try {
                    unsubscribe = onMessage(messaging, (payload) => {
                        console.log('Message received in foreground: ', payload);
                        const { title, body } = payload.notification;
                        // Native browser notification for foreground
                        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                            navigator.serviceWorker.ready.then((registration) => {
                                registration.showNotification(title, { body, icon: '/logo192.png' });
                            });
                        }
                    });
                } catch (e) {
                    console.warn("Failed to attach onMessage listener", e);
                }
            }
        }, 1000);

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            unsubscribe();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Function to handle navigation and set active page
    const showPage = (pageId, options = {}) => {
        // Handle special navigation cases like logout
        if (pageId === 'logout') {
            handleLogout();
            return;
        }

        // Force login for action pages
        const requireLoginPages = ['cart', 'orders', 'track-order', 'profile', 'labs', 'upload-prescription', 'health-insurance', 'payment'];
        if (requireLoginPages.includes(pageId)) {
            if (!isAuthenticated) {
                // Save where the user was trying to go so we can redirect after login/registration
                setPendingRedirect(pageId);
                setActivePage('login');
                return;
            }
        }

        // If navigating to login manually (e.g. from a navbar button), remember the current page
        // so we can return to it after registration completes
        if (pageId === 'login' && activePage !== 'login') {
            setPendingRedirect(activePage);
        }

        // When navigating to the products page, check if we should clear filters
        if (pageId === 'products' && !options.keepFilters) {
            setSearchTerm('');
            setSelectedTherapyAreaFilter('');
            setCurrentPage(1); // Also reset to the first page
        }
        setActivePage(pageId);
        setMessage(''); // Clear message on page change
    };

    const requestNotificationPermission = async () => {
        try {
            if (typeof Notification === 'undefined') return;
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                if (!messaging) return; // Prevent crashes if messaging is unsupported
                const vapidKey = process.env.REACT_APP_VAPID_KEY;
                const currentToken = await getToken(messaging, vapidKey ? { vapidKey } : undefined);
                if (currentToken) {
                    await callApi('/notifications/register-token', 'POST', { token: currentToken });
                }
            }
        } catch (error) {
            console.error('An error occurred while retrieving token. ', error);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('userToken'); // Clear the token from storage
        localStorage.removeItem('userInfo'); // Clear user info from storage
        setIsAuthenticated(false);
        setUserRole('user');
        setCurrentUserId(null);
        setCurrentUserMobile(null);
        setCurrentUserName(null);
        setCurrentUserAddress(null);
        setCurrentUserWalletBalance(0);
        setCart([]); // Clear the cart on logout
        showMessage('You have been logged out.', 'success');
        setActivePage('login'); // Redirect to login page
    };

    const handleSendOtp = async (mobile) => {
        setAuthError('');
        setPendingMobile(mobile);



        // Safely clear any dangling recaptcha instances to prevent DUPE errors
        if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch(e) {}
            window.recaptchaVerifier = null;
        }
        
        // Remove old container if it exists
        const oldContainer = document.getElementById('recaptcha-container');
        if (oldContainer) oldContainer.remove();

        const containerId = 'recaptcha-container';
        const recaptchaDiv = document.createElement('div');
        recaptchaDiv.id = containerId;
        document.body.appendChild(recaptchaDiv);

        try {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
                size: 'invisible'
            });

            const confirmation = await signInWithPhoneNumber(auth, mobile, window.recaptchaVerifier);
            setConfirmationResult(confirmation);
            setOtpStep(true);
            showMessage('OTP sent successfully.', 'success');

        } catch (error) {
            console.error('Error sending OTP:', error);
            
            if (error.code === 'auth/too-many-requests') {
                setAuthError('Too many attempts. Firebase has temporarily blocked your device. Please try again in 5 minutes.');
            } else if (error.code === 'auth/invalid-app-credential' || error.code === 'auth/captcha-check-failed') {
                setAuthError('reCAPTCHA failed. Please refresh the page and try again.');
            } else {
                setAuthError(error.message || 'Failed to send OTP. Make sure to include country code (+91).');
            }
            
            // Clean up the DOM node on failure
            if (window.recaptchaVerifier) {
                try { window.recaptchaVerifier.clear(); } catch(e) {}
                window.recaptchaVerifier = null;
            }
            recaptchaDiv.remove();
        }
    };

    const handleVerifyOtp = async (otpCode) => {
        setAuthError('');
        try {
            const result = await confirmationResult.confirm(otpCode);
            const user = result.user;
            // Get Firebase ID token
            const idToken = await user.getIdToken();

            if (forgotPasswordStep) {
                // Intercept flow for forgot password
                setPendingIdToken(idToken);
                showMessage('OTP Verified. Please enter your new MPIN.', 'success');
                return;
            }

            // Send to our backend to get standard JWT and user details
            const response = await callApi('/users/firebase-login', 'POST', { idToken, mobile: pendingMobile });

            if (response.isNewUser) {
                // New user - move to registration step
                setPendingIdToken(idToken);
                setOtpStep(false); // Clear OTP step so the registration form renders correctly
                setRegistrationStep(true);
                showMessage(response.message || 'Please complete your registration.', 'info');
            } else if (response.token && response.user) {
                localStorage.setItem('userToken', response.token);
                localStorage.setItem('userInfo', JSON.stringify(response.user));

                setIsAuthenticated(true);
                setCurrentUserId(response.user._id);
                if (response.user.role) {
                    setUserRole(response.user.role);
                } else if (response.user.isAdmin) {
                    setUserRole('admin');
                } else {
                    setUserRole('user');
                }
                setCurrentUserMobile(response.user.mobile || null);
                setCurrentUserName(response.user.name || null);
                setCurrentUserAddress(response.user.address || null);
                setCurrentUserWalletBalance(response.user.walletBalance || 0);
                showMessage(response.message || 'Login successful!', 'success');

                setOtpStep(false);
                setConfirmationResult(null);

                if (pendingRedirect) {
                    showPage(pendingRedirect);
                    setPendingRedirect(null);
                } else if (response.user.role === 'admin' || response.user.role === 'super_admin' || response.user.isAdmin) {
                    showPage('admin-orders');
                } else {
                    showPage('products');
                }
                
                // Request push notifications
                requestNotificationPermission();
            } else {
                setAuthError(response.message || 'Failed to login via backend.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setAuthError(error.message || 'Invalid OTP or server error.');
        }
    };

    const handlePasswordLogin = async (mobile, mpin) => {
        setAuthError('');
        try {
            const response = await callApi('/users/login', 'POST', { mobile, mpin });
            
            if (response.token && response.user) {
                localStorage.setItem('userToken', response.token);
                localStorage.setItem('userInfo', JSON.stringify(response.user));

                setIsAuthenticated(true);
                setCurrentUserId(response.user._id);
                if (response.user.role) {
                    setUserRole(response.user.role);
                } else if (response.user.isAdmin) {
                    setUserRole('admin');
                } else {
                    setUserRole('user');
                }
                setCurrentUserMobile(response.user.mobile || null);
                setCurrentUserName(response.user.name || null);
                setCurrentUserAddress(response.user.address || null);
                setCurrentUserWalletBalance(response.user.walletBalance || 0);
                showMessage(response.message || 'Login successful!', 'success');

                if (pendingRedirect) {
                    showPage(pendingRedirect);
                    setPendingRedirect(null);
                } else if (response.user.role === 'admin' || response.user.role === 'super_admin' || response.user.isAdmin) {
                    showPage('admin-orders');
                } else if (response.user.role === 'driver') {
                    showPage('driver-dashboard');
                } else {
                    showPage('products');
                }
            } else {
                setAuthError(response.message || 'Invalid credentials or server error.');
                return true; // Signal failure to caller
            }
        } catch (error) {
            console.error('Error logging in with MPIN:', error);
            const msg = error.message || '';
            if (msg.toLowerCase().includes('mpin') || msg.toLowerCase().includes('invalid')) {
                setAuthError('Incorrect MPIN. Please try again.');
            } else {
                setAuthError(msg || 'Login failed. Please try again.');
            }
            return true; // Signal failure to caller
        }
    };

    const clearAuthError = useCallback(() => setAuthError(''), []);

    const handleCompleteRegistration = async (userData) => {
        setAuthError('');
        try {
            let idToken = pendingIdToken;
            if (auth && auth.currentUser) {
                try {
                    idToken = await auth.currentUser.getIdToken(true);
                } catch (tokErr) {
                    console.warn('Could not refresh token from auth.currentUser, using pendingIdToken:', tokErr);
                }
            }

            const response = await callApi('/users/complete-registration', 'POST', { 
                idToken, 
                ...userData 
            });

            if (response.token && response.user) {
                localStorage.setItem('userToken', response.token);
                localStorage.setItem('userInfo', JSON.stringify(response.user));

                setIsAuthenticated(true);
                setCurrentUserId(response.user._id);
                if (response.user.role) {
                    setUserRole(response.user.role);
                } else if (response.user.isAdmin) {
                    setUserRole('admin');
                } else {
                    setUserRole('user');
                }
                setCurrentUserMobile(response.user.mobile || null);
                setCurrentUserName(response.user.name || null);
                setCurrentUserAddress(response.user.address || null);
                setCurrentUserWalletBalance(response.user.walletBalance || 0);
                showMessage(response.message || 'Registration successful!', 'success');

                setOtpStep(false);
                setRegistrationStep(false);
                setPendingIdToken(null);
                setConfirmationResult(null);

                const destination = pendingRedirect || 'landing';
                setPendingRedirect(null);
                showPage(destination);
            } else {
                setAuthError(response.message || 'Failed to complete registration.');
            }
        } catch (error) {
            console.error('Error completing registration:', error);
            setAuthError(error.message || 'Error completing registration. Please try again.');
        }
    };

    const handleResetPassword = async (newMpin) => {
        setAuthError('');
        try {
            const response = await callApi('/users/reset-password', 'POST', { 
                idToken: pendingIdToken, 
                newMpin,
                mobile: pendingMobile // Provide fallback for DEV bypass
            });

            if (response.success) {
                showMessage(response.message || 'MPIN reset successful! Please login with your new MPIN.', 'success');
                setForgotPasswordStep(false);
                setOtpStep(false);
                setPendingIdToken(null);
                // Return to login screen
            } else {
                setAuthError(response.message || 'Failed to reset MPIN.');
            }
        } catch (error) {
            console.error('Error resetting MPIN:', error);
            setAuthError(error.message || 'Error resetting MPIN. Please try again.');
        }
    };

    // Global function to show messages
    const showMessage = useCallback((msg, type = 'info') => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => setMessage(''), 5000); // Clear message after 5 seconds
    }, []);

    // Callback for adding to cart, passed down to ProductView
    const addToCart = useCallback((productToAdd) => {
        const name = productToAdd.productName || productToAdd.Product || productToAdd.name;
        const price = productToAdd.mrp || productToAdd.MRP || productToAdd.price;
        const imageUrl = getCleanImageUrl(productToAdd, name);

        const cartItem = {
            productId: productToAdd._id,
            name: name,
            price: price,
            quantity: 1,
            imageUrl: imageUrl,
            packageVolume: productToAdd.packageVolume || '',
            pack: productToAdd.pack || productToAdd.Pack || productToAdd.packSize || '',
            unit: productToAdd.unit || productToAdd.Unit || productToAdd.measurementUnit || ''
        };

        if (cartItem.price === null || cartItem.price === undefined) {
            console.error("Attempted to add product without MRP to cart:", productToAdd);
            showMessage('This product does not have a defined price and cannot be added to the cart.', 'error');
            return;
        }

        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.productId === cartItem.productId);
            if (existingItem) {
                return prevCart.map(item =>
                    item.productId === cartItem.productId ? { ...item, quantity: item.quantity + 1 } : item
                );
            } else {
                return [...prevCart, cartItem];
            }
        });
        showMessage(`${cartItem.name} added to cart!`, 'success');
    }, [showMessage]);

    // Function to set order details for payment
    const setOrderForPayment = useCallback(({ orderId, total }) => {
        setCurrentOrderId(orderId);
        setCurrentOrderTotal(total);
    }, []);

    // Fetch products from backend with search, filter, and pagination parameters
    // Simplified: no 'isFeatured' parameter needed anymore
    const fetchProducts = useCallback(async () => {
        setIsLoading(true); // Set loading to true before fetching
        try {
            let queryParams = new URLSearchParams();
            if (searchTerm) {
                queryParams.append('search', searchTerm);
            }
            if (selectedTherapyAreaFilter) {
                queryParams.append('category', selectedTherapyAreaFilter);
            }

            // If no search and no specific category, fetch random items for discovery
            if (!searchTerm && !selectedTherapyAreaFilter) {
                queryParams.append('random', 'true');
            }

            queryParams.append('page', currentPage);

            const url = `/v2/products?${queryParams.toString()}`;
            const { products: fetchedProducts, currentPage: backendCurrentPage, totalPages: backendTotalPages, totalProducts: backendTotalProducts } = await callApi(url, 'GET');

            if (fetchedProducts && Array.isArray(fetchedProducts)) {
                setProducts(fetchedProducts);
                setCurrentPage(backendCurrentPage);
                setTotalPages(backendTotalPages);
                setTotalProducts(backendTotalProducts);
                console.log("Products successfully loaded from backend with search/filter.");
            } else {
                setProducts([]);
                setTotalProducts(0);
                setTotalPages(0);
                console.warn("Received unexpected data structure for products:", fetchedProducts);
                showMessage('Failed to load products or received unexpected data.', 'error');
            }
        } catch (error) {
            setProducts([]);
            setTotalProducts(0);
            setTotalPages(0);
            showMessage(error.message || 'Error connecting to the product server.', 'error');
        } finally {
            setIsLoading(false); // Set loading to false after fetch is complete
        }
    }, [searchTerm, selectedTherapyAreaFilter, currentPage, showMessage]);


    // Trigger fetch for regular products when search term, filter, or page changes
    useEffect(() => {
        fetchProducts(); // Always fetch regular products
    }, [fetchProducts]);

    // Scroll to top when navigation occurs (e.g. going to a product details page)
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activePage, selectedProductId]);

    return (
        <div className="min-h-screen flex flex-col bg-blue-50">
            <div id="recaptcha-container"></div>
            <Header
                showPage={showPage}
                cartItemCount={cart.reduce((sum, item) => sum + item.quantity, 0)}
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                userName={currentUserName}
                userLocation={userLocation}
                userCoordinates={userCoordinates}
            />

            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            

            <main className="flex-grow container mx-auto mt-8 mb-24 md:mb-12">
                {activePage === 'login' &&
                    <LoginRegisterView
                        handleSendOtp={handleSendOtp}
                        handleVerifyOtp={handleVerifyOtp}
                        handleCompleteRegistration={handleCompleteRegistration}
                        handlePasswordLogin={handlePasswordLogin}
                        handleResetPassword={handleResetPassword}
                        otpStep={otpStep}
                        registrationStep={registrationStep}
                        forgotPasswordStep={forgotPasswordStep}
                        setForgotPasswordStep={setForgotPasswordStep}
                        pendingIdToken={pendingIdToken}
                        authError={authError}
                        clearAuthError={clearAuthError}
                    />
                }

                {activePage === 'health-insurance' &&
                    <HealthInsuranceFormView
                        showPage={showPage}
                        userName={currentUserName}
                        userFamilyMembers={currentUserFamilyMembers}
                        showMessage={showMessage}
                    />
                }

                {activePage === 'labs' && (
                    <LabsView 
                        showPage={showPage} 
                        isAuthenticated={isAuthenticated} 
                        currentUser={{
                            name: currentUserName,
                            mobile: currentUserMobile,
                            _id: currentUserId,
                            address: currentUserAddress,
                            addresses: currentUserAddresses
                        }}  
                        addToCart={addToCart}
                        showMessage={showMessage}
                    />
                )}

                {/* --- Main Dashboard Views --- */}
                {activePage === 'profile' &&
                    <ProfileView
                        showMessage={showMessage}
                        isAuthenticated={isAuthenticated}
                        showPage={showPage}
                        currentUserWalletBalance={currentUserWalletBalance}
                    />
                }
                {activePage === 'landing' &&
                    <LandingPageView 
                        showPage={showPage} 
                        products={products} 
                        addToCart={addToCart}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        setSelectedTherapyAreaFilter={setSelectedTherapyAreaFilter}
                        userLocation={userLocation}
                        setUserLocation={setUserLocation}
                        userCoordinates={userCoordinates}
                        setUserCoordinates={setUserCoordinates}
                    />
                }
                {activePage === 'products' && (
                    <ProductView
                        products={products}
                        currentPage={currentPage}
                        isLoading={isLoading}
                        totalProducts={totalProducts}
                        totalPages={totalPages}
                        searchTerm={searchTerm}
                        selectedTherapyAreaFilter={selectedTherapyAreaFilter}
                        setCurrentPage={setCurrentPage}
                        setSearchTerm={setSearchTerm}
                        setSelectedTherapyAreaFilter={setSelectedTherapyAreaFilter}
                        addToCart={addToCart}
                        userLocation={userLocation}
                        setUserLocation={setUserLocation}
                        userCoordinates={userCoordinates}
                        setUserCoordinates={setUserCoordinates}
                    />
                )}
                {activePage === 'product-details' && (
                    <ProductDetailsView
                        productId={selectedProductId}
                        addToCart={addToCart}
                        showMessage={showMessage}
                    />
                )}
                {activePage === 'cart' &&
                    <CartView
                        cart={cart}
                        setCart={setCart}
                        isAuthenticated={isAuthenticated}
                        currentUserId={currentUserId}
                        currentUserName={currentUserName}
                        currentUserMobile={currentUserMobile}
                        currentUserAddress={currentUserAddress}
                        currentUserAddresses={currentUserAddresses}
                        currentUserWalletBalance={currentUserWalletBalance}
                        showPage={showPage}
                        showMessage={showMessage}
                        setOrderForPayment={setOrderForPayment}
                    />
                }
                {activePage === 'payment' &&
                    <PaymentView
                        showPage={showPage}
                        currentOrderId={currentOrderId}
                        currentOrderTotal={currentOrderTotal}
                        currentOrderStoreBillUrl={currentOrderStoreBillUrl}
                        showMessage={showMessage}
                    />
                }
                {activePage === 'upload-prescription' &&
                    <UploadPrescriptionView
                        isAuthenticated={isAuthenticated}
                        showPage={showPage}
                        showMessage={showMessage}
                        addToCart={addToCart}
                        currentUser={{
                            name: currentUserName,
                            mobile: currentUserMobile,
                            address: currentUserAddress,
                            addresses: currentUserAddresses
                        }}
                    />
                }
                {activePage === 'orders' &&
                    <OrdersView
                        isAuthenticated={isAuthenticated}
                        showMessage={showMessage}
                    />
                }
                {activePage === 'track-order' &&
                    <TrackOrderView
                        showMessage={showMessage}
                    />
                }
                {activePage === 'admin-orders' &&
                    <AdminView
                        isAuthenticated={isAuthenticated}
                        showMessage={showMessage}
                        userRole={userRole}
                    />
                }
                {activePage === 'driver-dashboard' &&
                    <DriverDashboard
                        showMessage={showMessage}
                    />
                }
                {activePage === 'about-us' && <AboutUsView />}
                {activePage === 'careers' && <CareersView />}
                {activePage === 'join-us' && <JoinUsView />}
                {activePage === 'corporates' && <CorporatesView />}
                {activePage === 'blog' && <BlogView />}
                {activePage === 'help-center' && <HelpCenterView />}
                {activePage === 'privacy-policy' && <PrivacyPolicyView />}
                {activePage === 'terms-conditions' && <TermsConditionsView />}
                {activePage === 'return-policy' && <ReturnPolicyView />}
            </main>

            <Footer showPage={showPage} userCoordinates={userCoordinates} />

            <CartBottomBar 
                cart={cart}
                showPage={showPage}
                activePage={activePage}
            />
            <BottomNav
                showPage={showPage}
                activePage={activePage}
                isAuthenticated={isAuthenticated}
                userRole={userRole}
            />
            
            <InstallAppBanner requestNotificationPermission={requestNotificationPermission} />

        </div>
    );
}

export default App;
